import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as joint from '@joint/plus';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {toast} from 'react-toastify';
import {CanvasEnvironment} from '../../../../utils/canvas/canvas-environment';
import * as canvasActions from '../../../../utils/canvas/canvas-actions';
import {makeComponentFaultTreeContainer} from '../../../../utils/canvas/fault-tree-container';
import {CanvasActionType} from '../../../../@types/canvas-action-type';
import {ResourceType} from '../../../../@types/resource-type';
import {Component} from '../../../../@types/component';
import Group from '../../../../@types/group';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import componentService from '../../../../services/component.service';
import {canEditComponentDraft, isComponentFaultTreeContainer} from '../../../../utils/component-utils';
import ComponentFaultTreeContextMenu from '../../menus/ComponentFaultTreeContextMenu';
import AddFaultTreeNodeModal from '../../modals/canvas/AddFaultTreeNode.component';
import EditFaultTreeNodeModal from '../../modals/canvas/EditFaultTreeNode.component';
import DeleteFaultTreeNodeModal from '../../modals/canvas/DeleteFaultTreeNode.component';
import ReorderChildrenModal from '../../modals/canvas/ReorderChildren.component';
import ModelTestingPanel, {
    MODEL_TESTING_TAB_REVIEW,
    MODEL_TESTING_TAB_TEST_REPORT,
} from '../../modals/canvas/ModelTesting.component';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

interface ComponentFaultTreeCanvasProps {
    component: Component;
    isActive: boolean;
    readonly: boolean;
    updateTab: (tabObject: {objectType: ResourceType; object: Component}) => void;
    // A version opened from History: shown as it was, so never refetched as the live Component.
    snapshot?: boolean;
}

type ModalType = 'add' | 'edit' | 'delete' | 'reorder';
type ModalState = Record<ModalType, boolean>;
const initialModalState: ModalState = {add: false, edit: false, delete: false, reorder: false};

/** Fake Event-Sequence-shaped object so `buildftGraphFromObject` can build a standalone tree. */
function ftShim(component: Component): any {
    return {uniqueId: component.uniqueId, quantificationMode: 'MODE_PROBABILITY', events: []};
}

/** Builds / rebuilds the whole fault tree graph for `component` into `env`. */
function renderFaultTree(env: CanvasEnvironment, component: Component): void {
    const {graph, paper, paperScroller} = env;
    if (!component.faultTree) {
        return;
    }
    paper.freeze();
    const built = canvasActions.buildftGraphFromObject(
        ftShim(component), component.faultTree as any, Infinity, new Set<number>(),
    );
    graph.resetCells(built.getCells());
    const layout = new joint.layout.TreeLayout({
        graph,
        direction: 'B' as const,
        siblingGap: 50,
        parentGap: 150,
    });
    const viewportRect = paperScroller.getVisibleArea().inflate(50);
    canvasActions.layoutAndFocus(layout, paperScroller, paper, viewportRect.center());
    paper.unfreeze();

    requestAnimationFrame(() => {
        if (!paper.el || !paper.el.parentNode) {
            return;
        }
        env.clearDecorationTracking();
        canvasActions.decorateVisibleElements(env, {addToolsFlag: true, skipViewportFilter: true});
        const [source] = graph.getSources();
        if (source) {
            paperScroller.centerElement(source);
        }
    });
}

/**
 * The JointJS canvas for a Component's fault tree -- the lean sibling of `JointJsCanvasEditor`.
 * Same canvas stack (`CanvasEnvironment`, `canvas-actions`, the fault-tree-node modals), fault
 * tree only: no Events, and only structural editing actions.
 */
export default function ComponentFaultTreeCanvas(props: ComponentFaultTreeCanvasProps): React.ReactElement {
    const {component, isActive, readonly, updateTab, snapshot = false} = props;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const envRef = useRef<CanvasEnvironment | null>(null);
    const [env, setEnv] = useState<CanvasEnvironment | null>(null);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<joint.dia.ElementView | null>(null);
    const [modals, setModals] = useState<ModalState>(initialModalState);
    const [modelTestingOpen, setModelTestingOpen] = useState<boolean>(false);
    const [modelTestingTab, setModelTestingTab] = useState<number>(MODEL_TESTING_TAB_REVIEW);

    const {
        getAdditionalResource, currentWorkspaceObject, setCurrentWorkspaceObject,
        currentCanvasAction, setCurrentCanvasAction, setCurrentCanvasSelectedNode, publishResourceUpdate,
        setCanvasNavigateFunction,
    } = useWorkspaceContext();

    // Lets the Review findings centre the model on the node they are about, the way they do for a
    // Safety View. Registered only while this canvas is active; the next active canvas overwrites
    // it, which is how the event sequence canvas handles the same registration.
    useEffect(() => {
        if (isActive && env) {
            setCanvasNavigateFunction(() => (elementId: string) =>
                canvasActions.navigateToFaultTreeNode(env, elementId));
        }
    }, [isActive, env, setCanvasNavigateFunction]);

    const groups: Group[] = (getAdditionalResource(ResourceType.GROUPS).resources as Group[]) ?? [];
    const canEdit: boolean = !readonly && canEditComponentDraft(component, groups);

    const openModal = (m: ModalType): void => setModals((prev) => ({...prev, [m]: true}));
    const closeModal = (m: ModalType): void => setModals((prev) => ({...prev, [m]: false}));

    const openContextMenu = useCallback((event: joint.dia.Event, targetElement: joint.dia.ElementView): void => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(targetElement);
    }, []);
    const closeContextMenu = useCallback((): void => setAnchorEl(null), []);

    const applyRefreshed = (refreshed: Component): void => {
        // The tab and the workspace object are the canvas route. The resource update below stays
        // the Component itself, because that is what the explorer and other listeners know.
        const workspaceObject = {objectType: ResourceType.COMPONENT_TREE, object: refreshed};
        setCurrentWorkspaceObject(workspaceObject);
        publishResourceUpdate({
            type: ResourceType.COMPONENT,
            id: refreshed.id,
            updatedData: refreshed,
            source: 'save',
        });
        updateTab(workspaceObject);
    };

    // Canvas creation -- once per mounted component.
    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
        if (envRef.current === null) {
            const environment = new CanvasEnvironment(
                containerRef.current,
                openContextMenu,
                setCurrentCanvasSelectedNode,
                setIsRunning,
                readonly,
            );
            environment.isTagsModeActive = false;
            environment.faultTreeContainer = makeComponentFaultTreeContainer(component, canEdit);
            envRef.current = environment;
            setEnv(environment);
        }
        if (component?.faultTree) {
            renderFaultTree(envRef.current, component);
        }

        return (): void => {
            if (envRef.current) {
                envRef.current.destroy();
                envRef.current = null;
            }
            setCurrentCanvasSelectedNode(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component.id, openContextMenu, readonly]);

    // Keep the container ref fresh (canEdit / uniqueId) as the component object changes.
    useEffect(() => {
        if (envRef.current) {
            envRef.current.faultTreeContainer = makeComponentFaultTreeContainer(component, canEdit);
        }
    }, [component, canEdit]);

    // Re-render the graph when a mutation publishes a refreshed Component.
    useEffect(() => {
        const environment = envRef.current;
        if (!isActive || !environment) {
            return;
        }
        if (
            environment.isDirty
            && isComponentFaultTreeContainer(currentWorkspaceObject?.objectType)
            && currentWorkspaceObject?.object?.id === component.id
        ) {
            const refreshed = currentWorkspaceObject!.object as Component;
            environment.isDirty = false;
            // Keep the tab's object (and other listeners) in sync -- the modals only set
            // currentWorkspaceObject, not the tab.
            updateTab({objectType: ResourceType.COMPONENT_TREE, object: refreshed});
            publishResourceUpdate({
                type: ResourceType.COMPONENT,
                id: refreshed.id,
                updatedData: refreshed,
                source: 'save',
            });
            const collapsedIds: number[] = environment.graph.getElements()
                .filter((cell) => cell.get('collapsed'))
                .map((cell) => cell.get('data')?.id)
                .filter((id): id is number => id != null);
            const selectedIds: number[] = environment.selection
                .map((cell) => cell.get('data')?.id)
                .filter((id): id is number => id != null);
            canvasActions.setSelection(environment, []);

            renderFaultTree(environment, refreshed);

            requestAnimationFrame(() => {
                environment.isRestoringState = true;
                collapsedIds.forEach((id) => {
                    const cell = environment.graph.getCell(`ft-${id}`);
                    const cellView = cell ? environment.paper.findViewByModel(cell) : null;
                    if (cellView) {
                        environment.paper.trigger('element:expand', cellView);
                    }
                });
                environment.isRestoringState = false;
                const selection = selectedIds
                    .map((id) => environment.graph.getCell(`ft-${id}`))
                    .filter((cell): cell is joint.dia.Element => cell != null && cell.isElement());
                canvasActions.setSelection(environment, selection);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, currentWorkspaceObject]);

    // Toolbar / context-menu actions.
    useEffect(() => {
        if (!isActive || !currentCanvasAction || currentCanvasAction.actionType === CanvasActionType.UNKNOWN) {
            return;
        }
        const environment = envRef.current;
        if (!environment) {
            return;
        }
        switch (currentCanvasAction.actionType) {
            case CanvasActionType.MODEL_TESTING:
                setModelTestingTab(MODEL_TESTING_TAB_REVIEW);
                setModelTestingOpen(true);
                break;
            case CanvasActionType.TEST_REPORT_MAPPING:
                setModelTestingTab(MODEL_TESTING_TAB_TEST_REPORT);
                setModelTestingOpen(true);
                break;
            case CanvasActionType.CANVAS_RECENTER:
                canvasActions.recenter(environment);
                break;
            case CanvasActionType.CANVAS_FIT_TO_VIEW:
                setIsRunning(true);
                setTimeout(() => {
                    canvasActions.fitToView(environment);
                    setIsRunning(false);
                }, 0);
                break;
            case CanvasActionType.CANVAS_ZOOM_IN:
                canvasActions.zoomIn(environment);
                break;
            case CanvasActionType.CANVAS_ZOOM_OUT:
                canvasActions.zoomOut(environment);
                break;
            case CanvasActionType.CANVAS_FULLSCREEN:
                canvasActions.fullScreen(environment);
                break;
            case CanvasActionType.CANVAS_RELOAD_FT:
            case CanvasActionType.CANVAS_REFRESH:
                if (snapshot) {
                    break;
                }
                setIsRunning(true);
                componentService.getComponent(component.id)
                    .then((refreshed) => {
                        environment.isDirty = true;
                        applyRefreshed(refreshed);
                    })
                    .catch((err: Error) => toast.error(err.message))
                    .finally(() => setIsRunning(false));
                break;
            case CanvasActionType.CANVAS_UNDO:
            case CanvasActionType.CANVAS_REDO: {
                // Draft history only: a published release, or a version opened from History, is read-only.
                if (!canEdit) {
                    break;
                }
                const undoing = currentCanvasAction.actionType === CanvasActionType.CANVAS_UNDO;
                setIsRunning(true);
                (undoing ? componentService.undo(component.id) : componentService.redo(component.id))
                    .then((refreshed) => {
                        environment.isDirty = true;
                        applyRefreshed(refreshed);
                        toast.success(undoing ? 'Undo completed successfully' : 'Redo completed successfully');
                    })
                    .catch((err: Error) => toast.error(err.message))
                    .finally(() => setIsRunning(false));
                break;
            }
            case CanvasActionType.NODE_PROPERTIES:
            case CanvasActionType.FAULTTREENODE_EDIT_NODE:
                openModal('edit');
                break;
            case CanvasActionType.FAULTTREENODE_ADD_NODE:
                openModal('add');
                break;
            case CanvasActionType.FAULTTREENODE_DELETE_NODE:
                openModal('delete');
                break;
            case CanvasActionType.FAULTTREENODE_REORDER_CHILDREN:
                openModal('reorder');
                break;
            case CanvasActionType.FAULTTREENODE_COPY_SUBTREE:
                if (environment.selection.length > 0) {
                    localStorage.copyContents = JSON.stringify({
                        type: 'fault_tree_node',
                        id: environment.selection[0].get('data').id,
                    });
                }
                break;
            case CanvasActionType.FAULTTREENODE_PASTE_SUBTREE: {
                if (
                    !canvasActions.canAddOrEditResource(environment)
                    || environment.selection.length === 0
                    || !localStorage.copyContents
                ) {
                    break;
                }
                const copied = JSON.parse(localStorage.copyContents);
                if (copied.type !== 'fault_tree_node') {
                    break;
                }
                setIsRunning(true);
                componentService.copyPasteFaultTreeNode(
                    component.id,
                    copied.id,
                    environment.selection[0].get('data').id,
                )
                    .then((refreshed) => {
                        environment.isDirty = true;
                        applyRefreshed(refreshed);
                        toast.success('Subtree pasted successfully');
                    })
                    .catch((err: Error) => toast.error(err.message))
                    .finally(() => setIsRunning(false));
                break;
            }
            default:
                break;
        }

        setTimeout(() => {
            setCurrentCanvasAction({actionType: CanvasActionType.UNKNOWN});
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCanvasAction, isActive]);

    const open = Boolean(anchorEl);
    const modalOpen = (m: ModalType): boolean =>
        modals[m] && env != null && env.selection[0]?.get('data') != null;

    return (
        <div id={`component-ft-canvas-${component.id}`}
            style={{
                width: '100vw', height: 'calc(100vh - 184px)', maxWidth: '100%', maxHeight: '100%',
                overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column',
            }}>
            <div ref={containerRef} className="canvas"
                style={{flex: 1, minHeight: 0, width: '100%', position: 'relative'}}>
                <Tooltip title="Model testing">
                    <IconButton
                        aria-label="Open model testing"
                        onClick={(): void => {
                            setModelTestingTab(MODEL_TESTING_TAB_REVIEW);
                            setModelTestingOpen(true);
                        }}
                        size="small"
                        sx={{position: 'absolute', top: 8, left: 8, zIndex: 1000,
                            backgroundColor: 'white', boxShadow: 1}}
                    >
                        <LightbulbOutlinedIcon />
                    </IconButton>
                </Tooltip>
                <Backdrop sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}} open={isRunning}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <ComponentFaultTreeContextMenu
                    elementView={anchorEl}
                    open={open}
                    handleClose={closeContextMenu}
                    env={env}
                />
                <AddFaultTreeNodeModal open={modalOpen('add')} env={env} onClose={(): void => closeModal('add')} />
                <EditFaultTreeNodeModal open={modalOpen('edit')} env={env} onClose={(): void => closeModal('edit')} />
                <DeleteFaultTreeNodeModal open={modalOpen('delete')} env={env} onClose={(): void => closeModal('delete')} />
                <ReorderChildrenModal open={modalOpen('reorder')} env={env} onClose={(): void => closeModal('reorder')} />
            </div>
            <ModelTestingPanel
                open={modelTestingOpen}
                scope={{kind: 'component', id: component.id, name: component.name}}
                onClose={(): void => setModelTestingOpen(false)}
                initialTab={modelTestingTab}
            />
        </div>
    );
}
